// PRD 11 §4.2 / §8.1 — client-side validation for the Add/Edit form.
//
// Per PRD §4.2 the required fields (title / area / sub-area / item / grade) are only enforced
// "at publish". Draft (임시저장) relaxes them — only the title length cap still applies. The
// `forPublish` flag selects the mode.
//
// The BLOCK rules are NOT part of that relaxation: they run for draft and publish alike
// (SHCT-448 ⑦). PRD §3.4's "[임시저장] 클릭 | 필수 검증 완화" relaxes the REQUIRED-field checks
// only; the 블록 검증 clause has no draft exemption, and a draft must not be able to persist
// content that could never be published.
//
// The block rules themselves live in `@/shared/utils/block-validation` — PRD 11 v1.4 specifies
// them as "3곳 통일·3곳 프로토 동일 구현" with 건강백과 (PRD 10) and 관리지침 (PRD 07), so they are
// owned once and this file only maps a violation to 상담매뉴얼's copy. What stays here is what
// PRD 11 defines differently: the 200-char 제목 cap, the 임시저장 relaxation, and "블록 0개도
// 저장 가능" (§3.6 / §4.2).

import type { TFunction } from 'i18next';

import {
  BLOCK_DESC_MAX,
  BLOCK_TITLE_MAX,
  IMAGE_ACCEPT,
  IMAGE_TITLE_MAX,
  MAX_BLOCKS,
  TEXT_BLOCK_MAX,
  VIDEO_TITLE_MAX,
  blockHasEmoji,
  firstBlockRuleViolation,
  withoutEmptyMediaBlocks,
  isAllowedVideoUrl,
  isHttpsUrl,
  isImageFileValid,
  plainTextLength,
  type BlockRuleCode,
} from '@/shared/utils/block-validation';
import { hasEmoji } from '@/shared/utils/emoji';

import type { ContentBlock, ManualGrade } from '../types/counseling-manual.types';

export type ManualFormFieldKey = 'title' | 'cascade' | 'grade' | 'blocks';

export type ManualFormErrors = Partial<Record<ManualFormFieldKey, string>>;

export interface ManualFormDraft {
  title: string;
  area: string;
  subArea: string;
  item: string;
  grade: ManualGrade | null;
  blocks: ContentBlock[];
}

// [공통] 00 §8 — 상담매뉴얼 제목 1~200 (개별 유지 확정, TITLE 100 예외).
const TITLE_MAX = 200;

// Re-exported so this module stays the feature's single validation entry point and the screen
// components keep importing from one place.
export {
  BLOCK_DESC_MAX,
  BLOCK_TITLE_MAX,
  IMAGE_ACCEPT,
  IMAGE_TITLE_MAX,
  MAX_BLOCKS,
  TEXT_BLOCK_MAX,
  VIDEO_TITLE_MAX,
  blockHasEmoji,
  isAllowedVideoUrl,
  isHttpsUrl,
  isImageFileValid,
  plainTextLength,
  withoutEmptyMediaBlocks,
};

// 상담매뉴얼's copy for each shared rule.
const BLOCK_ERROR_KEYS: Record<BlockRuleCode, string> = {
  TOO_MANY_BLOCKS: 'errors.TOO_MANY_BLOCKS',
  EMPTY_BLOCK: 'errors.EMPTY_BLOCK',
  TEXT_BLOCK_TOO_LONG: 'errors.TEXT_BLOCK_TOO_LONG',
  VIDEO_URL_INVALID: 'errors.VIDEO_URL_INVALID',
  VIDEO_TITLE_INVALID: 'errors.VIDEO_TITLE_INVALID',
  IMAGE_TITLE_INVALID: 'errors.IMAGE_TITLE_INVALID',
  BLOCK_DESC_TOO_LONG: 'errors.BLOCK_DESC_TOO_LONG',
  EMOJI_NOT_ALLOWED: 'common:errors.emojiNotAllowed',
};

export function validateManualForm(
  draft: ManualFormDraft,
  t: TFunction,
  forPublish: boolean,
): { ok: boolean; errors: ManualFormErrors } {
  const errors: ManualFormErrors = {};

  const title = draft.title.trim();
  if (forPublish && !title) {
    errors.title = t('errors.TITLE_REQUIRED');
  } else if (title.length > TITLE_MAX) {
    errors.title = t('errors.TITLE_TOO_LONG');
  } else if (hasEmoji(title)) {
    // SHCT-856 — [공통] 00 §4.2: 이모지 전 클래스 불허. Like the block rules this is NOT part of
    // the 임시저장 relaxation — a draft must not be able to hold text that could never be published.
    errors.title = t('common:errors.emojiNotAllowed');
  }

  if (forPublish) {
    if (!draft.area || !draft.subArea || !draft.item) {
      errors.cascade = t('errors.CASCADE_REQUIRED');
    }
    if (!draft.grade) {
      errors.grade = t('errors.GRADE_REQUIRED');
    }
  }

  // PRD §3.6 / §4.2 — 블록 0개도 저장 가능; the shared rules only bite on blocks that exist.
  // SHCT-662 / PRD 11 v1.10 §3 — judge the array the save will actually send: an image block
  // with no upload and a video block with no URL are dropped, so they must not raise
  // EMPTY_BLOCK here either.
  const blocks = withoutEmptyMediaBlocks(draft.blocks);
  if (blocks.length > 0) {
    const violation = firstBlockRuleViolation(blocks);
    if (violation) {
      errors.blocks = t(BLOCK_ERROR_KEYS[violation.code], { max: violation.max });
    }
  }

  return { ok: Object.keys(errors).length === 0, errors };
}
